import React, { useState, useEffect, useContext, useRef } from 'react';
import { API, AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { toast } from 'sonner';
import { ClipboardCheck, AlertTriangle, Calendar, Lock, Save, RotateCcw, Pencil, User, Building } from 'lucide-react';
import PdfDownloadButton from '@/components/PdfDownloadButton';

// Cut-off checklist sections with items (updated based on user images)
const cutoffSections = [
  {
    id: 'special_orders',
    title: 'Special Orders',
    items: [
      { id: 'so-1', text: 'All special orders invoiced (where customer has collected)' },
      { id: 'so-2', text: 'Stock received against COS but not collected - stock transferred to a store location (COS still attached to stock for identification)' },
      { id: 'so-3', text: 'All un-invoiced COS stock (awaiting customer collection) identified - kept together in COS area for counting' }
    ]
  },
  {
    id: 'direct_deliveries',
    title: 'Direct Deliveries',
    items: [
      { id: 'dd-1', text: 'Identify any direct deliveries scheduled for count day that have been delivered prior to cut off' },
      { id: 'dd-2', text: 'Contact supplier and customer to confirm delivery was made' },
      { id: 'dd-3', text: 'Obtain proof of delivery from supplier' },
      { id: 'dd-4', text: 'Invoice the customer where required' }
    ]
  },
  {
    id: 'document_usage',
    title: 'Document Usage',
    items: [
      { id: 'du-1', text: 'All manual invoice/credit note books - unused forms reconciled and accounted for' },
      { id: 'du-2', text: 'All documents processed into SAP' }
    ]
  },
  {
    id: 'manual_dispatch',
    title: 'Manual Dispatch Notes',
    items: [
      { id: 'md-1', text: 'All dispatch notes accounted for and processed' }
    ]
  },
  {
    id: 'inventory_adjustments',
    title: 'Inventory Adjustments',
    items: [
      { id: 'ia-1', text: 'All stock adjustments (Reason Codes) entered' },
      { id: 'ia-2', text: 'All QI issues resolved' }
    ]
  },
  {
    id: 'community_involvement',
    title: 'Community Involvement',
    items: [
      { id: 'ci-1', text: 'All community involvement donations processed' }
    ]
  },
  {
    id: 'stock_receiving_irna',
    title: 'Stock Receiving & IRNA',
    items: [
      { id: 'sr-1', text: 'Continue to process EGR up until cut off time - 4:00 pm EST' },
      { id: 'sr-2', text: "All suppliers' stock received and processed into EGR (no unprocessed invoices)" },
      { id: 'sr-3', text: 'Print IRNA report and reconcile - investigate and action outstanding items' },
      { id: 'sr-4', text: 'IPC (International Procurement Centre) stock processed where applicable' }
    ]
  },
  {
    id: 'store_transfers',
    title: 'Store Transfers (eTOS)',
    items: [
      { id: 'st-1', text: 'All inward and outward eTOS transfers physically processed' },
      { id: 'st-2', text: 'All outward eTOS transfer LPNs confirmed in ABAP system' },
      { id: 'st-3', text: 'All inward eTOS transfers - receive LPN in ABAP system' },
      { id: 'st-4', text: 'Internal transfer report reconciled' }
    ]
  },
  {
    id: 'dc_transfers',
    title: 'DC Transfers',
    items: [
      { id: 'dc-1', text: 'All DC deliveries processed (pallets unpacked and LPNs confirmed)' }
    ]
  },
  {
    id: 'centralised_returns',
    title: 'Centralised Returns Centre (CRC)',
    items: [
      { id: 'crc-1', text: 'All CRC stock built to a batch with LPN produced and uploaded to CRC portal' },
      { id: 'crc-2', text: 'Stock ready for collection (not counted in stocktake)' },
      { id: 'crc-3', text: 'Stock NOT built to a batch (no LPN) must be counted as part of the stocktake' }
    ]
  }
];

// Signature Canvas Component
const SignatureCanvas = ({ value, onChange, disabled }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    if (value) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0);
      img.src = value;
    }
  }, [value]);

  const startDrawing = (e) => {
    if (disabled) return;
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    ctx.beginPath();
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
  };

  const draw = (e) => {
    if (!isDrawing || disabled) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
    ctx.strokeStyle = '#1a1a1a';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (isDrawing && !disabled) {
      setIsDrawing(false);
      const canvas = canvasRef.current;
      onChange(canvas.toDataURL());
    }
  };

  const clearSignature = () => {
    if (disabled) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    onChange('');
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        width={350}
        height={120}
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
        className={`border-2 border-dashed border-slate-300 rounded-lg bg-white ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-crosshair'}`}
        style={{ touchAction: 'none' }}
      />
      <button
        type="button"
        onClick={clearSignature}
        disabled={disabled}
        className="text-sm text-slate-500 hover:text-slate-700 mt-2 disabled:opacity-50"
      >
        Clear Signature
      </button>
    </div>
  );
};

const AdminCutoff = () => {
  const { user } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    count_date: '',
    store_id: '',
    items: {},
    is_submitted: false,
    submitted_at: null,
    submitted_by: null,
    leadership_signature: {
      name: '',
      date: new Date().toISOString().split('T')[0],
      signature: ''
    },
    manager_signature: {
      name: '',
      date: new Date().toISOString().split('T')[0],
      signature: ''
    }
  });
  const [stores, setStores] = useState([]);
  const [stocktakes, setStocktakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('token');

  const totalItems = cutoffSections.reduce((sum, section) => sum + section.items.length, 0);
  const completedItems = Object.values(formData.items).filter(item => item?.completed).length;
  const progressPercentage = Math.round((completedItems / totalItems) * 100);

  // Check if user can reset the form (Network Admin or Regional Manager only)
  const canResetForm = user?.role === 'network_admin' || user?.role === 'regional_manager';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [storesRes, stocktakesRes] = await Promise.all([
        fetch(`${API}/stores`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/stocktakes`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      const storesData = await storesRes.json();
      const stocktakesData = await stocktakesRes.json();
      setStores(storesData);
      setStocktakes(stocktakesData);

      // Load saved form data
      const savedRes = await fetch(`${API}/admin-cutoff`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (savedRes.ok) {
        const savedData = await savedRes.json();
        if (savedData && Object.keys(savedData).length > 0) {
          setFormData(prev => ({
            ...prev,
            ...savedData,
            items: savedData.items || {},
            leadership_signature: savedData.leadership_signature || prev.leadership_signature,
            manager_signature: savedData.manager_signature || prev.manager_signature
          }));
        }
      }
    } catch (error) {
      console.error(error);
      // Load from localStorage as fallback
      const saved = localStorage.getItem('admin_cutoff_form');
      if (saved) {
        setFormData(JSON.parse(saved));
      }
    } finally {
      setLoading(false);
    }
  };

  const updateItem = (itemId, field, value) => {
    if (formData.is_submitted) {
      toast.error('Form is locked and cannot be edited');
      return;
    }

    setFormData(prev => ({
      ...prev,
      items: {
        ...prev.items,
        [itemId]: {
          ...prev.items[itemId],
          [field]: value,
          updated_at: new Date().toISOString()
        }
      }
    }));
  };

  const toggleItemComplete = (itemId) => {
    const currentItem = formData.items[itemId] || {};
    updateItem(itemId, 'completed', !currentItem.completed);
  };

  const saveProgress = async () => {
    // Save to localStorage
    localStorage.setItem('admin_cutoff_form', JSON.stringify(formData));

    // Save to backend
    try {
      await fetch(`${API}/admin-cutoff`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      toast.success('Progress saved');
    } catch (error) {
      toast.error('Failed to save to server, saved locally');
    }
  };

  const handleSubmit = async () => {
    if (completedItems < totalItems) {
      toast.error(`Please complete all ${totalItems} items before submitting`);
      return;
    }

    if (!formData.count_date || !formData.store_id) {
      toast.error('Please fill in Count Day Date and Store');
      return;
    }

    if (!formData.leadership_signature.name || !formData.leadership_signature.signature) {
      toast.error('Leadership Team Member signature is required');
      return;
    }

    if (!formData.manager_signature.name || !formData.manager_signature.signature) {
      toast.error('Complex/Coordinator signature is required');
      return;
    }

    if (!window.confirm('Once submitted, this form cannot be edited (except by Network Admin or Regional Manager). Are you sure you want to submit?')) {
      return;
    }

    const submittedData = {
      ...formData,
      is_submitted: true,
      submitted_at: new Date().toISOString(),
      submitted_by: user?.name || 'Unknown'
    };

    setFormData(submittedData);
    localStorage.setItem('admin_cutoff_form', JSON.stringify(submittedData));

    try {
      await fetch(`${API}/admin-cutoff`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(submittedData)
      });
      toast.success('Form submitted and locked successfully');
    } catch (error) {
      toast.error('Failed to submit form');
    }
  };

  const handleReset = async () => {
    if (!canResetForm) {
      toast.error('Only Network Admin or Regional Manager can reset this form');
      return;
    }

    if (!window.confirm('Are you sure you want to reset this form? All data will be cleared and the form will be unlocked for re-submission.')) {
      return;
    }

    const resetData = {
      count_date: '',
      store_id: formData.store_id, // Keep the store
      items: {},
      is_submitted: false,
      submitted_at: null,
      submitted_by: null,
      reset_at: new Date().toISOString(),
      reset_by: user?.name,
      leadership_signature: {
        name: '',
        date: new Date().toISOString().split('T')[0],
        signature: ''
      },
      manager_signature: {
        name: '',
        date: new Date().toISOString().split('T')[0],
        signature: ''
      }
    };

    setFormData(resetData);
    localStorage.setItem('admin_cutoff_form', JSON.stringify(resetData));

    try {
      await fetch(`${API}/admin-cutoff`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(resetData)
      });
      toast.success('Form has been reset');
    } catch (error) {
      toast.error('Failed to reset form on server');
    }
  };

  const getSectionProgress = (section) => {
    const completed = section.items.filter(item => formData.items[item.id]?.completed).length;
    return { completed, total: section.items.length };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-cutoff-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <ClipboardCheck className="w-10 h-10 text-teal-600 mt-1" />
          <div>
            <h1 className="text-4xl font-bold text-slate-900 tracking-tight uppercase">Admin Cut-Off Sign-Off</h1>
            <p className="text-slate-600">Stocktake Administration Cut-offs Sign Off Sheet</p>
          </div>
        </div>
        <PdfDownloadButton 
          contentId="admin-cutoff-content" 
          filename="Admin_CutOff_Form" 
          title="Admin Cut-Off Sign-Off Form"
        />
      </div>

      <div id="admin-cutoff-content">
        {/* Important Notice */}
        <Card className="bg-amber-50 border-amber-200 p-6 mb-6">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
          <div>
            <h3 className="font-semibold text-amber-900 mb-2">Important Notice</h3>
            <ul className="text-sm text-amber-800 space-y-1">
              <li>• This form can only be submitted <strong>once per site per stocktake</strong></li>
              <li>• Once submitted, the form is <strong>locked and cannot be edited</strong></li>
              <li>• Only <strong>Network Admin</strong> or <strong>Regional Manager</strong> can reset a submitted form</li>
              <li>• Both leadership team member and manager signatures are required</li>
              <li>• Cut off time is <strong>4:00 PM EST</strong> on count day</li>
            </ul>
          </div>
        </div>
      </Card>

      {/* Task Completion Progress */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-2">
          <div>
            <p className="text-sm text-slate-600">Task Completion</p>
            <p className="text-3xl font-bold text-slate-900">{completedItems} / {totalItems}</p>
          </div>
          <Badge className={`px-4 py-2 text-sm ${
            progressPercentage === 100 ? 'bg-green-600' : 'bg-slate-800'
          }`}>
            {progressPercentage}% Complete
          </Badge>
        </div>
        <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
          <div
            className={`h-full transition-all duration-500 ${
              progressPercentage === 100 ? 'bg-green-500' : 'bg-teal-500'
            }`}
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
      </Card>

      {/* Count Day Information */}
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <Calendar className="w-5 h-5 text-slate-700" />
          <h2 className="text-lg font-semibold text-slate-900">Count Day Information</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Count Day Date</label>
            <Input
              type="date"
              value={formData.count_date}
              onChange={(e) => setFormData({ ...formData, count_date: e.target.value })}
              disabled={formData.is_submitted}
              className="w-full"
              data-testid="count-date-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Store</label>
            <Select 
              value={formData.store_id || ""} 
              onValueChange={(val) => setFormData({ ...formData, store_id: val })}
              disabled={formData.is_submitted}
            >
              <SelectTrigger data-testid="store-select">
                <SelectValue placeholder="Select store" />
              </SelectTrigger>
              <SelectContent>
                {stores.map((store) => (
                  <SelectItem key={store.id} value={store.id}>
                    {store.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {/* Checklist Sections */}
      {cutoffSections.map((section) => {
        const progress = getSectionProgress(section);
        return (
          <Card key={section.id} className="p-6" data-testid={`section-${section.id}`}>
            <div className="mb-4">
              <h2 className="text-xl font-semibold text-slate-900">{section.title}</h2>
              <p className="text-sm text-slate-500">{progress.completed} of {progress.total} completed</p>
            </div>

            <div className="space-y-4">
              {section.items.map((item) => {
                const itemData = formData.items[item.id] || {};
                const isCompleted = itemData.completed;

                return (
                  <Card 
                    key={item.id} 
                    className={`p-4 border-2 transition-colors ${
                      isCompleted ? 'border-teal-200 bg-teal-50/30' : 'border-slate-200'
                    }`}
                    data-testid={`item-${item.id}`}
                  >
                    <div className="flex items-start gap-4">
                      <Checkbox
                        checked={isCompleted || false}
                        onCheckedChange={() => toggleItemComplete(item.id)}
                        disabled={formData.is_submitted}
                        className="mt-1 w-6 h-6 border-2 border-teal-500 data-[state=checked]:bg-teal-500"
                        data-testid={`checkbox-${item.id}`}
                      />
                      <div className="flex-1">
                        <p className={`font-medium ${isCompleted ? 'text-slate-500' : 'text-slate-900'}`}>
                          {item.text}
                        </p>
                        
                        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 mt-3">
                          <div>
                            <label className="block text-xs text-slate-500 mb-1">Comments</label>
                            <Input
                              placeholder="Add comments..."
                              value={itemData.comments || ''}
                              onChange={(e) => updateItem(item.id, 'comments', e.target.value)}
                              disabled={formData.is_submitted}
                              className="text-sm"
                              data-testid={`comments-${item.id}`}
                            />
                          </div>
                          <div className="w-20">
                            <label className="block text-xs text-slate-500 mb-1">Initials</label>
                            <Input
                              placeholder="XX"
                              value={itemData.initials || ''}
                              onChange={(e) => updateItem(item.id, 'initials', e.target.value.toUpperCase())}
                              disabled={formData.is_submitted}
                              maxLength={3}
                              className="text-sm text-center uppercase"
                              data-testid={`initials-${item.id}`}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </Card>
        );
      })}

      {/* Sign-off Authorization Section */}
      <Card className="p-6 border-2 border-teal-200" data-testid="signoff-section">
        <div className="flex items-center gap-2 mb-2">
          <Pencil className="w-5 h-5 text-teal-600" />
          <h2 className="text-xl font-semibold text-slate-900">Sign-off Authorization</h2>
        </div>
        <p className="text-sm text-slate-500 mb-6">Both signatures are required to complete this form</p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Leadership Team Member */}
          <Card className="p-6 border border-slate-200">
            <div className="flex items-center gap-2 mb-4">
              <User className="w-5 h-5 text-slate-600" />
              <h3 className="font-semibold text-slate-900">Leadership Team Member</h3>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Name</label>
                <Input
                  placeholder="Enter full name"
                  value={formData.leadership_signature?.name || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    leadership_signature: { ...formData.leadership_signature, name: e.target.value }
                  })}
                  disabled={formData.is_submitted}
                  data-testid="leadership-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Date</label>
                <Input
                  type="date"
                  value={formData.leadership_signature?.date || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    leadership_signature: { ...formData.leadership_signature, date: e.target.value }
                  })}
                  disabled={formData.is_submitted}
                  data-testid="leadership-date"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center gap-1">
                  <Pencil className="w-4 h-4" /> Signature
                </label>
                <SignatureCanvas
                  value={formData.leadership_signature?.signature || ''}
                  onChange={(sig) => setFormData({
                    ...formData,
                    leadership_signature: { ...formData.leadership_signature, signature: sig }
                  })}
                  disabled={formData.is_submitted}
                />
              </div>
            </div>
          </Card>

          {/* Complex/Coordinator */}
          <Card className="p-6 border border-slate-200">
            <div className="flex items-center gap-2 mb-4">
              <Building className="w-5 h-5 text-slate-600" />
              <h3 className="font-semibold text-slate-900">Complex/Coordinator</h3>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Name</label>
                <Input
                  placeholder="Enter full name"
                  value={formData.manager_signature?.name || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    manager_signature: { ...formData.manager_signature, name: e.target.value }
                  })}
                  disabled={formData.is_submitted}
                  data-testid="manager-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Date</label>
                <Input
                  type="date"
                  value={formData.manager_signature?.date || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    manager_signature: { ...formData.manager_signature, date: e.target.value }
                  })}
                  disabled={formData.is_submitted}
                  data-testid="manager-date"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center gap-1">
                  <Pencil className="w-4 h-4" /> Signature
                </label>
                <SignatureCanvas
                  value={formData.manager_signature?.signature || ''}
                  onChange={(sig) => setFormData({
                    ...formData,
                    manager_signature: { ...formData.manager_signature, signature: sig }
                  })}
                  disabled={formData.is_submitted}
                />
              </div>
            </div>
          </Card>
        </div>
      </Card>

      {/* Action Buttons */}
      <div className="flex items-center justify-between bg-white rounded-xl border border-slate-200 p-6">
        {formData.is_submitted ? (
          <div className="flex-1">
            <div className="flex items-center gap-3 text-green-700 mb-2">
              <Lock className="w-5 h-5" />
              <span className="font-medium">
                Form submitted by {formData.submitted_by} on {new Date(formData.submitted_at).toLocaleString()}
              </span>
            </div>
            {canResetForm && (
              <Button 
                variant="outline" 
                onClick={handleReset}
                className="text-orange-600 hover:bg-orange-50 hover:text-orange-700"
                data-testid="reset-form-btn"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Reset Form (Admin Only)
              </Button>
            )}
          </div>
        ) : (
          <>
            <Button 
              variant="outline" 
              onClick={saveProgress}
              data-testid="save-progress-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              Save Progress
            </Button>
            <Button 
              onClick={handleSubmit}
              disabled={completedItems < totalItems}
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="submit-form-btn"
            >
              <Lock className="w-4 h-4 mr-2" />
              Submit Sign-off
            </Button>
          </>
        )}
      </div>
      </div>
    </div>
  );
};

export default AdminCutoff;
